import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { useModal } from '../../context/ModalContext';
import { QRCodeSVG } from 'qrcode.react';
import { FiCopy, FiDownload, FiMessageSquare, FiSave } from 'react-icons/fi';

const DEFAULT_MESSAGE = 'Hi, I would like to see the menu.';

function normalizePhoneNumber(value) {
  return (value || '').replace(/[^\d]/g, '');
}

function applyMessagePlaceholders(message, restaurantName) {
  if (!message) return '';
  return message.replace(/\{restaurant\}/gi, restaurantName || 'our restaurant');
}

function sanitizeFileName(value, fallback = 'qr') {
  const text = String(value || '').trim();
  const cleaned = text.replace(/[^a-zA-Z0-9._-]/g, '_');
  return cleaned || fallback;
}

function WhatsAppMenu() {
  const { alert } = useModal();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [restaurantSettings, setRestaurantSettings] = useState(null);
  const [tables, setTables] = useState([]);
  const [loadingTables, setLoadingTables] = useState(false);
  const [selectedTableId, setSelectedTableId] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/settings/restaurant');
        setRestaurantSettings({
          ...response.data,
          whatsapp_menu_message: response.data?.whatsapp_menu_message || DEFAULT_MESSAGE
        });
      } catch (error) {
        await alert('Failed to load WhatsApp menu settings', 'Error', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [alert]);

  // Load tables for per-table WhatsApp QRs (when QR ordering is enabled)
  useEffect(() => {
    const fetchTables = async () => {
      try {
        setLoadingTables(true);
        const response = await api.get('/api/tables');
        const data = response.data || [];
        setTables(data);
        if (data.length > 0) {
          setSelectedTableId(data[0].id);
        }
      } catch (error) {
        // Silent fail – table-specific section will just show an info message
      } finally {
        setLoadingTables(false);
      }
    };

    fetchTables();
  }, []);

  const whatsappLink = useMemo(() => {
    if (!restaurantSettings) return '';
    const phone = normalizePhoneNumber(restaurantSettings.whatsapp_menu_phone);
    const message = applyMessagePlaceholders(
      restaurantSettings.whatsapp_menu_message || DEFAULT_MESSAGE,
      restaurantSettings.name
    );
    if (!phone) return '';
    return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  }, [restaurantSettings]);

  const selectedTable = useMemo(
    () => tables.find((t) => t.id === selectedTableId) || tables[0],
    [tables, selectedTableId]
  );

  const tableWhatsAppLink = useMemo(() => {
    if (!restaurantSettings || !selectedTable) return '';
    if (restaurantSettings.qrOrderingEnabled === false) return '';
    const phone = normalizePhoneNumber(restaurantSettings.whatsapp_menu_phone);
    if (!phone) return '';

    const restaurantName = restaurantSettings.name || 'our restaurant';
    const tableNumber = selectedTable.table_number || '1';
    const msg = `Hi, I am seated at ${restaurantName} and seated at table ${tableNumber}. Can I get the menu?`;
    return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
  }, [restaurantSettings, selectedTable]);

  const handleSave = async () => {
    if (!restaurantSettings) return;
    try {
      setSaving(true);
      await api.put('/api/settings/restaurant', restaurantSettings);
      await alert('WhatsApp menu settings saved', 'Success', 'success');
    } catch (error) {
      await alert(error.response?.data?.error || 'Failed to save settings', 'Error', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyLink = async () => {
    if (!whatsappLink) return;
    await navigator.clipboard.writeText(whatsappLink);
    await alert('WhatsApp link copied to clipboard', 'Success', 'success');
  };

  const downloadQrPng = async (svgId, fileBaseName) => {
    const svgElement = document.getElementById(svgId);
    if (!svgElement) {
      await alert('QR not available for download', 'Error', 'error');
      return;
    }

    const svgClone = svgElement.cloneNode(true);
    svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

    const serializer = new XMLSerializer();
    const svgMarkup = serializer.serializeToString(svgClone);
    const svgBlob = new Blob([svgMarkup], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);

    try {
      const image = new Image();
      image.decoding = 'async';

      const loaded = await new Promise((resolve, reject) => {
        image.onload = () => resolve(true);
        image.onerror = () => reject(new Error('Failed to load QR image'));
        image.src = svgUrl;
      });

      if (!loaded) {
        throw new Error('Failed to process QR image');
      }

      const qrSize = Number(svgElement.getAttribute('width') || 220);
      const exportSize = Math.max(1600, qrSize * 8);

      const canvas = document.createElement('canvas');
      canvas.width = exportSize;
      canvas.height = exportSize;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Canvas not supported');
      }

      ctx.imageSmoothingEnabled = false;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, exportSize, exportSize);
      ctx.drawImage(image, 0, 0, exportSize, exportSize);

      const pngUrl = canvas.toDataURL('image/png', 1.0);
      const link = document.createElement('a');
      link.href = pngUrl;
      link.download = `${sanitizeFileName(fileBaseName)}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      await alert(error.message || 'Failed to download QR', 'Error', 'error');
    } finally {
      URL.revokeObjectURL(svgUrl);
    }
  };

  if (loading || !restaurantSettings) {
    return (
      <Layout>
        <div className="p-8 text-center text-gray-600">Loading WhatsApp menu settings...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-8 space-y-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gray-100">
            <FiMessageSquare className="h-5 w-5 text-gray-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">WhatsApp Menu</h1>
            <p className="text-gray-600">Generate a QR that opens WhatsApp with a preset menu request.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">WhatsApp Number *</label>
                <input
                  type="tel"
                  value={restaurantSettings.whatsapp_menu_phone || ''}
                  onChange={(e) => setRestaurantSettings({
                    ...restaurantSettings,
                    whatsapp_menu_phone: e.target.value
                  })}
                  placeholder="+919876543210"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Use full country code. Example: +91 9876543210</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Preset Message</label>
                <textarea
                  rows={4}
                  value={restaurantSettings.whatsapp_menu_message || ''}
                  onChange={(e) => setRestaurantSettings({
                    ...restaurantSettings,
                    whatsapp_menu_message: e.target.value
                  })}
                  placeholder="Hi, I am {restaurant}. Can I get the menu?"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">You can use {`{restaurant}`} to insert the restaurant name.</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Menu Link (for bot reply)</label>
                <input
                  type="url"
                  value={restaurantSettings.whatsapp_menu_menu_url || ''}
                  onChange={(e) => setRestaurantSettings({
                    ...restaurantSettings,
                    whatsapp_menu_menu_url: e.target.value
                  })}
                  placeholder="https://yourdomain.com/menu"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Optional. Store the menu URL you’ll use in WhatsApp replies.</p>
              </div>

              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full bg-gray-900 text-white rounded-lg px-4 py-2 flex items-center justify-center gap-2 hover:bg-gray-800 disabled:opacity-60"
              >
                <FiSave className="h-4 w-4" />
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-3">
              <h2 className="text-lg font-semibold text-gray-900">Preview</h2>
              <div className="text-sm text-gray-600">
                <div className="font-medium text-gray-700">WhatsApp Link</div>
                {whatsappLink ? (
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      readOnly
                      value={whatsappLink}
                      onClick={(e) => e.target.select()}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm"
                    />
                    <button
                      onClick={handleCopyLink}
                      className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 flex items-center gap-2"
                    >
                      <FiCopy className="h-4 w-4" />
                      Copy
                    </button>
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-gray-500">Enter a WhatsApp number to generate a link.</p>
                )}
              </div>
              <div className="text-sm text-gray-600">
                <div className="font-medium text-gray-700">Message Preview</div>
                <p className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
                  {applyMessagePlaceholders(
                    restaurantSettings.whatsapp_menu_message || DEFAULT_MESSAGE,
                    restaurantSettings.name
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* Generic WhatsApp Menu QR (view-only / fallback) */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 flex flex-col items-center justify-center gap-4">
              <div className="text-center">
                <h2 className="text-lg font-semibold text-gray-900">Generic WhatsApp Menu QR</h2>
                <p className="text-sm text-gray-500">
                  Scan to open WhatsApp with your preset menu request (no table attached).
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                {whatsappLink ? (
                  <QRCodeSVG id="whatsapp-menu-generic-qr" value={whatsappLink} size={220} level="H" includeMargin />
                ) : (
                  <div className="w-[220px] h-[220px] flex items-center justify-center text-sm text-gray-400">
                    Add a WhatsApp number to generate QR
                  </div>
                )}
              </div>
              {whatsappLink && (
                <button
                  onClick={() => downloadQrPng('whatsapp-menu-generic-qr', 'generic')}
                  className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 flex items-center gap-2 text-sm"
                >
                  <FiDownload className="h-4 w-4" /> Download QR
                </button>
              )}
            </div>

            {/* Per-table WhatsApp QR (only when QR ordering is enabled) */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Per‑Table WhatsApp QRs</h2>
                  <p className="text-xs text-gray-500 mt-1">
                    Works only when QR ordering is enabled. Each table gets its own WhatsApp QR and message.
                  </p>
                </div>
              </div>

              {restaurantSettings.qrOrderingEnabled === false ? (
                <p className="text-xs text-gray-500">
                  QR ordering is currently <span className="font-semibold text-red-600">disabled</span> for this
                  tenant. Enable the <strong>qr_ordering</strong> module to use table‑specific WhatsApp QRs.
                </p>
              ) : !normalizePhoneNumber(restaurantSettings.whatsapp_menu_phone || '') ? (
                <p className="text-xs text-gray-500">
                  Configure a WhatsApp number above to generate table‑specific WhatsApp QRs.
                </p>
              ) : loadingTables ? (
                <p className="text-xs text-gray-500">Loading tables...</p>
              ) : tables.length === 0 ? (
                <p className="text-xs text-gray-500">
                  No tables found. Add tables in the <strong>Tables</strong> section first.
                </p>
              ) : (
                <>
                  {/* Table selector */}
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-gray-700">Select Table</label>
                    <select
                      value={selectedTableId || tables[0]?.id}
                      onChange={(e) => setSelectedTableId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    >
                      {tables.map((table) => (
                        <option key={table.id} value={table.id}>
                          Table {table.table_number}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedTable && tableWhatsAppLink && (
                    <div className="space-y-3">
                      <div className="flex flex-col items-center gap-3">
                        <div className="bg-white p-4 rounded-lg border border-gray-200 inline-block shadow-sm">
                          <QRCodeSVG
                            id="whatsapp-menu-table-qr"
                            value={tableWhatsAppLink}
                            size={220}
                            level="H"
                            includeMargin
                          />
                        </div>
                        <button
                          onClick={() => downloadQrPng('whatsapp-menu-table-qr', String(selectedTable.table_number || 'table'))}
                          className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 flex items-center gap-2 text-sm"
                        >
                          <FiDownload className="h-4 w-4" /> Download QR
                        </button>
                        <div className="w-full space-y-2">
                          <label className="block text-xs font-semibold text-gray-700">
                            WhatsApp Link for Table {selectedTable.table_number}
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              readOnly
                              value={tableWhatsAppLink}
                              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 text-xs focus:outline-none focus:ring-2 focus:ring-gray-900"
                              onClick={(e) => e.target.select()}
                            />
                            <button
                              onClick={async () => {
                                await navigator.clipboard.writeText(tableWhatsAppLink);
                                await alert('WhatsApp link copied to clipboard!', 'Success', 'success');
                              }}
                              className="px-3 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 flex items-center gap-1.5 transition-all shadow-sm text-xs"
                            >
                              <FiCopy className="h-3 w-3" />
                              Copy
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Example of preset message for this table */}
                      <div className="border border-gray-200 rounded-lg bg-gray-50 p-3 text-xs text-gray-700">
                        <div className="font-semibold text-gray-800 mb-1">
                          Example WhatsApp message for this table:
                        </div>
                        <div>
                          Hi, I am seated at{' '}
                          <span className="font-semibold">
                            {restaurantSettings.name || 'our restaurant'}
                          </span>{' '}
                          and seated at table{' '}
                          <span className="font-semibold">{selectedTable.table_number}</span>. Can I
                          get the menu?
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default WhatsAppMenu;
